/**
 * Text analyzer — extracts pedagogically-meaningful features from student
 * writing. Pure function, no side effects. Safe to run on every save.
 *
 * Feature categories mirror the achievement catalog:
 *  - volume      : counts (words, sentences, paragraphs)
 *  - voice       : first/second person, opinion + reasoning, counterview
 *  - showDontTell: sensory words, proper nouns, numbers, measurements
 *  - storyCraft  : setting markers, dialogue, character introduction
 *  - wordCraft   : vivid verbs, feeling words, long words
 *  - connections : transitions, signal words, causal/example connectors
 *  - discoveries : "I wonder", similes, repetition
 *
 * Detection is intentionally rule-based (regex + curated word lists) rather
 * than NLP — false negatives (missing a celebration) are cheap; false
 * positives are costly because they cheapen the moment.
 */

import {
  SENSORY_WORDS,
  VIVID_VERBS,
  FEELING_WORDS,
  SETTING_PHRASES,
  TRANSITION_WORDS,
  COUNTERVIEW_MARKERS,
  CLAIM_WITH_REASON_REGEX,
  CAUSAL_REGEX,
  EXAMPLE_CONNECTOR_REGEX,
  FIRST_PERSON_REGEX,
  SECOND_PERSON_REGEX,
  WONDER_REGEX,
  SIMILE_REGEX,
  DIALOGUE_REGEX,
  PROPER_NOUN_REGEX,
  NUMBER_REGEX,
  MEASUREMENT_REGEX,
} from './wordLists';

export interface TextFeatures {
  // ----- Volume -----
  wordCount: number;
  uniqueWordCount: number;
  sentenceCount: number;
  paragraphCount: number;
  longestSentenceWords: number;
  longestParagraphSentences: number;

  // ----- Voice & Stance -----
  hasFirstPerson: boolean;
  hasSecondPerson: boolean;
  hasClaimWithReason: boolean;
  hasCounterView: boolean;

  // ----- Show, Don't Tell -----
  sensoryWordCount: number;
  properNounCount: number;
  hasNumber: boolean;
  hasMeasurement: boolean;

  // ----- Story Craft -----
  hasSettingMarker: boolean;
  hasDialogue: boolean;

  // ----- Word Craft -----
  longestWordLength: number;
  bigWordCount: number;        // 8+ letter words (proxy for sophisticated vocab)
  vividVerbCount: number;
  feelingWordCount: number;

  // ----- Connections -----
  transitionWordCount: number;
  hasCausalConnection: boolean;
  hasExampleConnector: boolean;
  signalWordsUsed: Set<string>;

  // ----- Discoveries (secret-ish) -----
  hasWonder: boolean;
  hasSimile: boolean;

  // Kept for any future use (no longer drives "first usage" achievements)
  punctuation: {
    period: number;
    comma: number;
    question: number;
    exclamation: number;
    quote: number;
    semicolon: number;
  };
}

/**
 * Analyze a body of text against an optional list of signal words from
 * the active writing plan.
 */
export function analyze(text: string, signalWords: string[] = []): TextFeatures {
  const trimmed = (text || '').trim();

  // ----- Words -----
  const wordMatches = trimmed.match(/[A-Za-z']+/g) ?? [];
  const wordCount = wordMatches.length;
  const lowered = wordMatches.map((w) => w.toLowerCase());
  const uniqueWordCount = new Set(lowered).size;
  const longestWordLength = wordMatches.reduce((max, w) => Math.max(max, w.length), 0);

  // ----- Sentences (split on terminal punctuation) -----
  const sentenceChunks = trimmed
    .split(/[.!?]+(?=\s|$)/)
    .map((s) => s.trim())
    .filter(Boolean);
  const sentenceCount = sentenceChunks.length;
  const longestSentenceWords = sentenceChunks.reduce((max, s) => {
    const ws = s.match(/[A-Za-z']+/g)?.length ?? 0;
    return Math.max(max, ws);
  }, 0);

  // ----- Paragraphs (split on blank lines) -----
  const paragraphChunks = trimmed
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const paragraphCount = paragraphChunks.length;
  const longestParagraphSentences = paragraphChunks.reduce((max, p) => {
    const sc = p.split(/[.!?]+(?=\s|$)/).map((s) => s.trim()).filter(Boolean).length;
    return Math.max(max, sc);
  }, 0);

  // ----- Voice & Stance -----
  const hasFirstPerson = FIRST_PERSON_REGEX.test(trimmed);
  const hasSecondPerson = SECOND_PERSON_REGEX.test(trimmed);
  const hasClaimWithReason = CLAIM_WITH_REASON_REGEX.test(trimmed);
  const hasCounterView = COUNTERVIEW_MARKERS.some((rx) => rx.test(trimmed));

  // ----- Show, Don't Tell -----
  let sensoryWordCount = 0;
  let vividVerbCount = 0;
  let feelingWordCount = 0;
  let bigWordCount = 0;
  for (const w of lowered) {
    if (SENSORY_WORDS.has(w)) sensoryWordCount++;
    if (VIVID_VERBS.has(w)) vividVerbCount++;
    if (FEELING_WORDS.has(w)) feelingWordCount++;
    if (w.length >= 8) bigWordCount++;
  }
  const properNounMatches = trimmed.match(PROPER_NOUN_REGEX);
  const properNounCount = properNounMatches ? properNounMatches.length : 0;
  const hasNumber = NUMBER_REGEX.test(trimmed);
  const hasMeasurement = MEASUREMENT_REGEX.test(trimmed);

  // ----- Story Craft -----
  const hasSettingMarker = SETTING_PHRASES.some((rx) => rx.test(trimmed));
  const hasDialogue = DIALOGUE_REGEX.test(trimmed);

  // ----- Connections -----
  let transitionWordCount = 0;
  for (const w of lowered) {
    if (TRANSITION_WORDS.has(w)) transitionWordCount++;
  }
  const hasCausalConnection = CAUSAL_REGEX.test(trimmed);
  const hasExampleConnector = EXAMPLE_CONNECTOR_REGEX.test(trimmed);

  // ----- Discoveries -----
  const hasWonder = WONDER_REGEX.test(trimmed);
  const hasSimile = SIMILE_REGEX.test(trimmed);

  // ----- Signal words from the writing plan -----
  const signalWordsUsed = new Set<string>();
  for (const sw of signalWords) {
    const swLower = sw.toLowerCase();
    const escaped = swLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const rx = new RegExp(`\\b${escaped}\\b`, 'i');
    if (rx.test(trimmed)) signalWordsUsed.add(swLower);
  }

  // ----- Punctuation (data only — no longer drives achievements) -----
  const punctuation = {
    period: (trimmed.match(/\./g) ?? []).length,
    comma: (trimmed.match(/,/g) ?? []).length,
    question: (trimmed.match(/\?/g) ?? []).length,
    exclamation: (trimmed.match(/!/g) ?? []).length,
    quote: Math.floor(((trimmed.match(/["“”]/g) ?? []).length) / 2),
    semicolon: (trimmed.match(/;/g) ?? []).length,
  };

  return {
    wordCount,
    uniqueWordCount,
    sentenceCount,
    paragraphCount,
    longestSentenceWords,
    longestParagraphSentences,
    hasFirstPerson,
    hasSecondPerson,
    hasClaimWithReason,
    hasCounterView,
    sensoryWordCount,
    properNounCount,
    hasNumber,
    hasMeasurement,
    hasSettingMarker,
    hasDialogue,
    longestWordLength,
    bigWordCount,
    vividVerbCount,
    feelingWordCount,
    transitionWordCount,
    hasCausalConnection,
    hasExampleConnector,
    signalWordsUsed,
    hasWonder,
    hasSimile,
    punctuation,
  };
}
